import { Directive, ElementRef, inject, input, output } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { delay, interval, Subject, switchMap, takeUntil, tap } from 'rxjs'

/**
 * `LongPressDirective` is an Angular directive that emits events based on long press actions.
 *
 * Selector: '[mosLongPress]'
 *
 * @example
 * <button mosLongPress (mosLongPress)="onLongPress()" (mosLongPressProgress)="onProgress($event)"></button>
 *
 * @property {number} mosLongPressDelay - The delay (in milliseconds) to wait before considering the press as a long press. Default is 1000ms.
 *
 * @event {void} mosLongPress - Event emitted when a long press is detected.
 * @event {number} mosLongPressProgress - Event emitted with the progress of the long press. The progress is a number between 0 and 1.
 *
 * The directive starts tracking the long press action when the 'mousedown' event is detected. It then emits the progress of the long press every 100ms.
 * If the 'mouseup' event is detected and the mouse is still inside the element, the directive checks if the progress is 1 (i.e., the mosLongPressDelay has passed), and if so, it emits the 'mosLongPress' event.
 * If the 'mouseup' event is detected and the mouse is outside the element, the directive resets the progress and stops tracking the long press action.
 */
@Directive({
	selector: '[mosLongPress]',
	host: {
		'(mousedown)': 'onMouseDown()',
		'(touchstart)': 'onMouseDown()',
		'(mouseup)': 'onMouseUp()',
		'(touchend)': 'onTouchEnd($event)',
		'(document:mouseup)': 'onDocumentMouseUp($event)',
		'(document:touchend)': 'onDocumentMouseUp($event)',
		'(contextmenu)': 'onContextMenu($event)',
	},
})
export class LongPressDirective {
	private elementRef = inject(ElementRef)

	protected press = new Subject<void>()
	protected release = new Subject<boolean>()
	protected progress = 0
	protected pressInterval = 100

	readonly mosLongPressDelay = input(1000)

	protected readonly mosLongPress = output<void>()
	readonly mosLongPressProgress = output<number>()

	constructor() {
		this.press
			.pipe(
				takeUntilDestroyed(),
				tap(() => this.updateProgress(1)),
				switchMap(() => interval(this.pressInterval).pipe(takeUntil(this.release))),
			)
			.subscribe((v) => {
				this.updateProgress(v + 2)
			})

		// delay(0) is used to make sure the contextmenu event is caught before the mosLongPress event is emitted
		this.release.pipe(takeUntilDestroyed(), delay(0)).subscribe((inside) => {
			if (inside && this.progress >= 1) {
				// TODO: The 'emit' function requires a mandatory void argument
				this.mosLongPress.emit()
			}
			this.progress = 0
			this.mosLongPressProgress.emit(this.progress)
		})
	}

	private updateProgress(value: number) {
		// Since we start at value = 1 from 0ms, we need to add 100ms to the delay to get the correct progress (and overall delay)
		// This is done so that the progress bar moves immediately when the user presses the button to give proper feedback
		this.progress =
			Math.min(value * this.pressInterval, this.mosLongPressDelay() + this.pressInterval) /
			(this.mosLongPressDelay() + this.pressInterval)
		this.mosLongPressProgress.emit(this.progress)
	}

	onMouseDown() {
		this.press.next()
	}

	onMouseUp() {
		this.release.next(true)
	}

	onTouchEnd(event: TouchEvent) {
		// touchend is shitty and triggers even outside the element and doesn't give the real target...
		// so we need to use document.elementFromPoint to get the real target and compare
		const target = document.elementFromPoint(event.changedTouches[0].clientX, event.changedTouches[0].clientY)
		if (this.elementRef && !this.elementRef.nativeElement.contains(target)) {
			this.release.next(false)
		} else {
			this.release.next(true)
		}
	}

	onDocumentMouseUp(event: MouseEvent | TouchEvent) {
		if (this.elementRef && !this.elementRef.nativeElement.contains(event.target)) {
			this.release.next(false)
		}
	}

	onContextMenu(event: Event) {
		event.preventDefault()
	}
}
